/**
 * Automation Block Schema
 *
 * Defines the data model for automation blocks that connect
 * database tables with workflows.
 *
 * Follows the Note-Reduce Pattern for correct canvas positioning.
 */

import type { FilterGroup } from '@blocksuite/affine/data-view';
import type { GfxCompatibleProps } from '@blocksuite/std/gfx';
import { GfxCompatible } from '@blocksuite/std/gfx';
import type { Text } from '@blocksuite/store';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

import type {
  BatchConfig,
  InputTable,
  MatchConfigProps,
  OutputMapping,
  OutputMode,
  OutputTable,
  RunStatus,
  StatusTrackingConfig,
  TriggerConfig,
} from './automation-types.js';
import {
  DEFAULT_BATCH_CONFIG,
  DEFAULT_STATUS_TRACKING_CONFIG,
  DEFAULT_TRIGGER_CONFIG,
} from './automation-types.js';

// =============================================================================
// Edgeless Props (Note-Reduce Pattern)
// =============================================================================

export type AutomationEdgelessProps = {
  style: {
    borderRadius: number;
    borderSize: number;
  };
  scale?: number;
  collapse?: boolean;
  collapsedHeight?: number;
};

/**
 * Local-first workflow projection used by the canvas card.
 *
 * The workflow backend remains authoritative, but the canvas must never fetch
 * workflow details while rendering multiple automation blocks. The sidebar
 * writes this compact ordered projection into the automation Y.Doc when a
 * workflow is selected or explicitly refreshed.
 */
export type AutomationWorkflowStepSummary = {
  id: string;
  name: string;
  type: string;
};

// =============================================================================
// Props Type
// =============================================================================

export type AutomationBlockProps = {
  // Meta
  title: Text;
  titleMode: 'auto' | 'custom';

  // Input Config
  sourceTableId: string | null;
  sourceTableName: string | null;
  /** E018-S008-T005: Doc ID of source table (for CrossDoc indicator) */
  sourceDocId: string | null;
  /** Data-View compatible filter group */
  filterGroup: FilterGroup;
  /** Selected column IDs to include in payload (legacy mode) */
  selectedColumns: string[];
  /** E007-T022: Schema-based input mappings (takes precedence over selectedColumns) */
  inputMappings: Array<{
    schemaFieldId: string;
    schemaFieldName?: string;
    /** Type of the workflow schema field (e.g. 'text', 'number') */
    schemaFieldType?: string;
    columnId: string | null;
    /** sl-m379: Which input table this column belongs to (references InputTable.id) */
    inputTableId?: string;
  }>;
  /** sl-m379: Multi-Input — lead table + secondary lookup tables */
  inputTables: InputTable[];

  // Process Config
  workflowId: string | null;
  /** Display name of the linked workflow (shown on canvas block title) */
  workflowName: string | null;
  /** Ordered, cacheable workflow-step projection for canvas rendering. */
  workflowSteps: AutomationWorkflowStepSummary[];
  /** Workflow ID the cached step projection belongs to (empty arrays included). */
  workflowStepsSourceId: string | null;
  /** Manual webhook URL override (takes precedence over workflow's URL) */
  webhookUrlOverride: string | null;
  workflowParams: Record<string, unknown>;

  // Output Config
  outputMode: OutputMode;
  /**
   * Multi-Output: List of target tables for writing results.
   */
  outputTables: OutputTable[];
  /**
   * Output field mappings: response field -> target column.
   * Each mapping specifies which table (from outputTables) and column to write to.
   */
  outputMappings: OutputMapping[];

  /**
   * sl-mau5: First-class match configurations for row matching and relation lookup.
   * Referenced by OutputTable.matchConfigId and OutputMapping.matchConfigId.
   */
  matchConfigs: MatchConfigProps[];

  // Batching & Error Handling (T037)
  batchConfig: BatchConfig;

  // Trigger Config (T036)
  triggerConfig: TriggerConfig;

  // Status Tracking (T044)
  statusTracking: StatusTrackingConfig;

  // Runtime
  lastRunAt: number | null;
  lastRunStatus: RunStatus;

  // Edgeless Props (Note-Reduce Pattern)
  edgeless: AutomationEdgelessProps;
} & GfxCompatibleProps;

// =============================================================================
// Model Class
// =============================================================================

export class AutomationBlockModel extends GfxCompatible<AutomationBlockProps>(
  BlockModel
) {
  /**
   * Check if the automation has a source table configured
   */
  get isConfigured(): boolean {
    return this.props.sourceTableId !== null;
  }

  /**
   * Get the number of active filters (manual + auto from status tracking)
   */
  get filterCount(): number {
    const group = this.props.filterGroup;
    const manualFilters =
      group && group.type === 'group' ? group.conditions.length : 0;
    // E018-T006: Count status tracking auto-filter
    const autoFilter = this.props.statusTracking?.enabled ? 1 : 0;
    return manualFilters + autoFilter;
  }

  /**
   * Get the number of selected columns
   */
  get selectedColumnCount(): number {
    return this.props.selectedColumns.length;
  }

  /**
   * Get the number of output mappings
   */
  get mappingCount(): number {
    return this.props.outputMappings?.length ?? 0;
  }

  /**
   * Check if the automation has a workflow assigned
   */
  get hasWorkflow(): boolean {
    return this.props.workflowId !== null;
  }

  /**
   * Check if the automation is currently running
   */
  get isRunning(): boolean {
    return this.props.lastRunStatus === 'running';
  }
}

// =============================================================================
// Default Values
// =============================================================================

// Single source of truth for automation block dimensions.
// Backend (automation-block.service.ts) must mirror these values manually.
export const DEFAULT_AUTOMATION_WIDTH = 320;
// Stable initial canvas footprint. Internal stages stretch to fill it; the
// measurement guard only persists later non-default geometry changes.
export const DEFAULT_AUTOMATION_HEIGHT = 563;
const DEFAULT_BORDER_RADIUS = 8;
const DEFAULT_BORDER_SIZE = 1;

// =============================================================================
// Schema Definition
// =============================================================================

export const AutomationBlockSchema = defineBlockSchema({
  flavour: 'affine:automation',
  props: (internal): AutomationBlockProps => ({
    // Meta
    title: internal.Text('New Automation'),
    titleMode: 'auto',

    // Input Config
    sourceTableId: null,
    sourceTableName: null,
    sourceDocId: null, // E018-S008-T005
    filterGroup: { type: 'group', op: 'and', conditions: [] },
    selectedColumns: [],
    inputMappings: [], // E007-T022
    inputTables: [], // sl-m379: Multi-Input

    // Process Config
    workflowId: null,
    workflowName: null,
    workflowSteps: [],
    workflowStepsSourceId: null,
    webhookUrlOverride: null,
    workflowParams: {},

    // Output Config
    outputMode: 'update_source',
    outputTables: [],
    outputMappings: [],
    matchConfigs: [], // sl-mau5

    // Batching & Error Handling
    batchConfig: { ...DEFAULT_BATCH_CONFIG },

    // Trigger Config
    triggerConfig: { ...DEFAULT_TRIGGER_CONFIG },

    // Status Tracking (T044)
    statusTracking: { ...DEFAULT_STATUS_TRACKING_CONFIG },

    // Runtime
    lastRunAt: null,
    lastRunStatus: null,

    // GfxCompatible props
    xywh: `[0,0,${DEFAULT_AUTOMATION_WIDTH},${DEFAULT_AUTOMATION_HEIGHT}]`,
    index: 'a0',
    lockedBySelf: false,

    // Edgeless Props (Note-Reduce Pattern)
    edgeless: {
      style: {
        borderRadius: DEFAULT_BORDER_RADIUS,
        borderSize: DEFAULT_BORDER_SIZE,
      },
    },
  }),
  metadata: {
    role: 'hub',
    version: 1,
    parent: ['affine:note', '@root'], // Note (Doc) + Canvas Root
    children: [], // No children for v1
  },
  toModel: () => new AutomationBlockModel(),
});

// =============================================================================
// Schema Extension
// =============================================================================

export const AutomationBlockSchemaExtension = BlockSchemaExtension(
  AutomationBlockSchema
);
